import { Link } from "react-router-dom";
import Footer from "../components/Footer";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Modern Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900 tracking-tight">Prindox</span>
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6 text-blue-600">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">Privacy Policy</h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Your privacy is our priority. Learn how we protect your data and respect your rights.
          </p>
          <p className="text-sm font-medium text-gray-500 mt-6 bg-white inline-block px-4 py-2 rounded-full border border-gray-200">Last updated: December 2024</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-blue mx-auto">
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-xl mb-12">
              <h3 className="text-blue-900 font-bold mt-0">Privacy-First Approach</h3>
              <p className="text-blue-800 mb-0">
                Prindox is built with privacy as a core principle. We collect only the minimum data necessary to provide our service, 
                and we automatically delete your documents to ensure complete privacy protection.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">Information We Collect</h2>
            
            <div className="space-y-8 mb-12">
              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">1</div>
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Documents You Upload</h3>
                  <ul className="text-gray-600 space-y-2 list-none pl-0">
                    <li className="flex items-start"><span className="mr-2 text-blue-500">•</span> <span><strong>Temporary Storage:</strong> Documents are stored only during the printing process</span></li>
                    <li className="flex items-start"><span className="mr-2 text-blue-500">•</span> <span><strong>Automatic Deletion:</strong> All files are permanently deleted after confirmation</span></li>
                    <li className="flex items-start"><span className="mr-2 text-blue-500">•</span> <span><strong>Encrypted Transfer:</strong> All documents are encrypted during upload and transfer</span></li>
                  </ul>
                </div>
              </div>

              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">2</div>
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Technical Information</h3>
                  <ul className="text-gray-600 space-y-2 list-none pl-0">
                    <li className="flex items-start"><span className="mr-2 text-blue-500">•</span> IP address (for security and service delivery)</li>
                    <li className="flex items-start"><span className="mr-2 text-blue-500">•</span> Browser type and version</li>
                    <li className="flex items-start"><span className="mr-2 text-blue-500">•</span> Device information (for compatibility)</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">Document Lifecycle</h2>
            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-sm mb-12">
              <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                <div className="flex items-center relative">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4 ring-4 ring-white z-10">1</div>
                  <div>
                    <strong className="text-gray-900 block">Upload</strong>
                    <span className="text-gray-600 text-sm">Document encrypted and temporarily stored</span>
                  </div>
                </div>
                <div className="flex items-center relative">
                  <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4 ring-4 ring-white z-10">2</div>
                  <div>
                    <strong className="text-gray-900 block">Processing</strong>
                    <span className="text-gray-600 text-sm">Shop receives notification and prints document</span>
                  </div>
                </div>
                <div className="flex items-center relative">
                  <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4 ring-4 ring-white z-10">3</div>
                  <div>
                    <strong className="text-gray-900 block">Confirmation</strong>
                    <span className="text-gray-600 text-sm">User confirms receipt of printed documents</span>
                  </div>
                </div>
                <div className="flex items-center relative">
                  <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4 ring-4 ring-white z-10">4</div>
                  <div>
                    <strong className="text-gray-900 block">Deletion</strong>
                    <span className="text-gray-600 text-sm">All copies permanently wiped from our servers</span>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">How We Protect Your Data</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Encryption</h3>
                <p className="text-gray-600 text-sm">All data is encrypted in transit using TLS 1.3 and at rest with AES-256.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access Controls</h3>
                <p className="text-gray-600 text-sm">Strict limitations on employee access and multi-factor authentication.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Data Minimization</h3>
                <p className="text-gray-600 text-sm">We only collect what's absolutely necessary for the service.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Regular Audits</h3>
                <p className="text-gray-600 text-sm">Frequent security assessments and penetration testing.</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h2>
            <div className="bg-slate-900 text-white p-8 rounded-2xl">
              <p className="mb-6 text-gray-300">
                If you have questions about this privacy policy or our data practices, please contact us:
              </p>
              <ul className="space-y-3 mb-0">
                <li className="flex items-center text-blue-200">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  privacy@scan2print.com
                </li>
                <li className="flex items-center text-gray-400 text-sm ml-8">
                  Response time: Within 48 hours
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}